import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PersonIcon from "@mui/icons-material/Person";
import React, { useEffect, useMemo, useState } from "react";
import { axiosClient } from "../AxiosClient";
import RelationPanel, { PRIMARY_COLOR } from "./RelationPanel";
import { AsesorFormDialog, CellphoneFormDialog, SedeFormDialog } from "./RelationFormDialogs";
import { formatCellphone, sortByNewest, toAsesor, toCellphone, toSede } from "./commerceRelations";

const TABS = { SEDES: 0, CELULARES: 1, ASESORES: 2 };
const SNACKBAR_DURATION_MS = 4000;

const avatarSx = { bgcolor: "#e8eaf6", color: PRIMARY_COLOR };

const getErrorMessage = (error, fallback) =>
    new Error(error.response?.data?.message || error.response?.data?.description || fallback);

const fetchList = (url, mapper) =>
    axiosClient
        .get(url)
        .then((response) => (Array.isArray(response.data) ? response.data.map(mapper) : []))
        .catch((error) => {
            console.log(`Error cargando ${url}:`, error.message);
            return [];
        });

const joinDetails = (details) => details.filter(Boolean).join(" · ");

const emptyToNull = (value) => value?.trim() || null;

function EditButton({ label, onClick }) {
    return (
        <Tooltip title="Editar">
            <IconButton edge="end" aria-label={label} onClick={onClick} sx={{ color: PRIMARY_COLOR }}>
                <EditIcon />
            </IconButton>
        </Tooltip>
    );
}

function RelationItem({ icon, primary, secondary, actions }) {
    return (
        <ListItem divider secondaryAction={<Stack direction="row" alignItems="center" spacing={1}>{actions}</Stack>}>
            <ListItemAvatar>
                <Avatar sx={avatarSx}>{icon}</Avatar>
            </ListItemAvatar>
            <ListItemText primary={primary} secondary={secondary} sx={{ pr: 12 }} />
        </ListItem>
    );
}

const countAdvisorsLabel = (count) => {
    if (count === 0) {
        return "Sin asesores asignados";
    }
    return count === 1 ? "1 asesor asignado" : `${count} asesores asignados`;
};

const getSedeSearchFields = (sede) => [sede.name, sede.address, sede.city, sede.department, sede.cellphone];
const getCellphoneSearchFields = (cellphone) => [cellphone.number];
const getAsesorSearchFields = (asesor) => [
    asesor.name,
    asesor.lastName,
    asesor.documentNumber,
    asesor.email,
    asesor.sedeLabel,
    asesor.cellphoneLabel,
];

export default function CommerceRelationsTabs({ commerceUuid, commerceOldId, cities, departaments }) {
    const [activeTab, setActiveTab] = useState(TABS.SEDES);
    const [isLoading, setIsLoading] = useState(true);
    const [sedes, setSedes] = useState([]);
    const [cellphones, setCellphones] = useState([]);
    const [asesores, setAsesores] = useState([]);
    const [dialog, setDialog] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");

    const sortedSedes = useMemo(() => sortByNewest(sedes), [sedes]);
    const sortedCellphones = useMemo(() => sortByNewest(cellphones), [cellphones]);
    const sortedAsesores = useMemo(() => sortByNewest(asesores), [asesores]);
    const savedSedes = useMemo(() => sortedSedes.filter((sede) => sede.uuid), [sortedSedes]);
    const savedCellphones = useMemo(() => sortedCellphones.filter((cellphone) => cellphone.uuid), [sortedCellphones]);
    const advisorsByCellphone = useMemo(
        () =>
            asesores.reduce(
                (counts, asesor) => ({
                    ...counts,
                    [asesor.commerceCellphoneUuid]: (counts[asesor.commerceCellphoneUuid] || 0) + 1,
                }),
                {}
            ),
        [asesores]
    );

    const loadSedes = () => fetchList(`/v1/store-commerces/by-commerce/${commerceOldId}`, toSede).then(setSedes);
    const loadCellphones = () =>
        fetchList(`/v1/commerce/cellphones/by-commerce/${commerceOldId}`, toCellphone).then((loadedCellphones) => {
            setCellphones(loadedCellphones);
            return loadedCellphones;
        });
    const loadAsesores = () => fetchList(`/v1/advisor/by-commerce/${commerceOldId}`, toAsesor).then(setAsesores);

    useEffect(() => {
        if (!commerceOldId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        Promise.all([loadSedes(), loadCellphones(), loadAsesores()]).finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commerceOldId]);

    const openCreate = (type) => setDialog({ type, item: null });
    const openEdit = (type, item) => setDialog({ type, item });
    const closeDialog = () => setDialog(null);
    const editingItem = dialog?.item;

    const buildSedeBody = (sede) => ({
        name: sede.name.trim(),
        address: sede.address.trim(),
        city: sede.city,
        department: sede.department,
        cellphone: sede.cellphone,
    });

    const handleCreateSede = (sede) =>
        axiosClient
            .post("/v1/store-commerces", { ...buildSedeBody(sede), commerceUuid })
            .then((response) => {
                setSedes((current) => [toSede(response.data), ...current]);
                setSuccessMessage("Sede creada correctamente");
            })
            .catch((error) => {
                throw getErrorMessage(error, "No se pudo crear la sede");
            });

    const handleUpdateSede = (sede) =>
        axiosClient
            .patch(`/v1/store-commerces/${editingItem.uuid}`, buildSedeBody(sede))
            .catch((error) => {
                throw getErrorMessage(error, "No se pudo actualizar la sede");
            })
            .then(() => Promise.all([loadSedes(), loadAsesores()]))
            .then(() => setSuccessMessage("Sede actualizada correctamente"));

    const handleCreateCellphone = ({ number }) =>
        axiosClient
            .post("/v1/commerce/cellphones/batch-save", {
                commerceId: commerceOldId ? parseInt(commerceOldId) : null,
                cellphones: [number],
            })
            .catch((error) => {
                throw getErrorMessage(error, "No se pudo guardar el celular");
            })
            .then(loadCellphones)
            .then((loadedCellphones) => {
                if (!loadedCellphones.some((cellphone) => cellphone.number === number)) {
                    throw new Error(`El ${formatCellphone(number)} ya está registrado en otro comercio`);
                }
                setSuccessMessage(`Celular ${formatCellphone(number)} guardado`);
            });

    const handleUpdateCellphone = ({ number }) =>
        axiosClient
            .patch(`/v1/commerce/cellphones/${editingItem.uuid}`, { cellphone: number })
            .catch((error) => {
                throw getErrorMessage(error, "No se pudo actualizar el celular");
            })
            .then(() => Promise.all([loadCellphones(), loadAsesores()]))
            .then(() => setSuccessMessage(`Celular actualizado a ${formatCellphone(number)}`));

    const buildAsesorBody = (asesor) => ({
        name: asesor.name.trim(),
        lastName: emptyToNull(asesor.lastName),
        documentNumber: emptyToNull(asesor.documentNumber),
        email: emptyToNull(asesor.email),
        storeCommerceUuid: asesor.storeCommerceUuid,
        commerceCellphoneUuid: asesor.commerceCellphoneUuid,
    });

    const handleCreateAsesor = (asesor) =>
        axiosClient
            .post("/v1/advisor", buildAsesorBody(asesor))
            .catch((error) => {
                throw getErrorMessage(error, "No se pudo crear el asesor");
            })
            .then(loadAsesores)
            .then(() => setSuccessMessage("Asesor creado correctamente"));

    const handleUpdateAsesor = (asesor) =>
        axiosClient
            .patch(`/v1/advisor/${editingItem.uuid}`, { ...buildAsesorBody(asesor), isActive: asesor.isActive })
            .catch((error) => {
                throw getErrorMessage(error, "No se pudo actualizar el asesor");
            })
            .then(loadAsesores)
            .then(() => setSuccessMessage("Asesor actualizado correctamente"));

    const renderSede = (sede) => (
        <RelationItem
            key={sede.uuid}
            icon={<StorefrontIcon />}
            primary={sede.name || sede.address}
            secondary={joinDetails([
                sede.name && sede.address,
                [sede.city, sede.department].filter(Boolean).join(", "),
                formatCellphone(sede.cellphone),
            ])}
            actions={<EditButton label={`Editar sede ${sede.name || sede.address}`} onClick={() => openEdit(TABS.SEDES, sede)} />}
        />
    );

    const renderCellphone = (cellphone) => (
        <RelationItem
            key={cellphone.uuid || cellphone.number}
            icon={<PhoneIphoneIcon />}
            primary={formatCellphone(cellphone.number)}
            secondary={countAdvisorsLabel(advisorsByCellphone[cellphone.uuid] || 0)}
            actions={
                <EditButton
                    label={`Editar celular ${formatCellphone(cellphone.number)}`}
                    onClick={() => openEdit(TABS.CELULARES, cellphone)}
                />
            }
        />
    );

    const renderAsesor = (asesor) => {
        const fullName = [asesor.name, asesor.lastName].filter(Boolean).join(" ");
        return (
            <RelationItem
                key={asesor.uuid}
                icon={<PersonIcon />}
                primary={joinDetails([fullName, asesor.documentNumber && `Doc. ${asesor.documentNumber}`])}
                secondary={joinDetails([asesor.sedeLabel, formatCellphone(asesor.cellphoneLabel), asesor.email])}
                actions={
                    <>
                        <Chip
                            size="small"
                            label={asesor.isActive ? "Activo" : "Inactivo"}
                            color={asesor.isActive ? "success" : "default"}
                            variant={asesor.isActive ? "filled" : "outlined"}
                        />
                        <EditButton label={`Editar asesor ${fullName}`} onClick={() => openEdit(TABS.ASESORES, asesor)} />
                    </>
                }
            />
        );
    };

    const missingAsesorRequirements = [
        savedSedes.length === 0 && "una sede",
        savedCellphones.length === 0 && "un celular",
    ].filter(Boolean);
    const asesorDisabledReason =
        missingAsesorRequirements.length > 0
            ? `Primero registra al menos ${missingAsesorRequirements.join(" y ")}`
            : "";

    const asesorRequirementsNotice = asesorDisabledReason && (
        <Alert
            severity="warning"
            action={
                <Button
                    color="inherit"
                    size="small"
                    onClick={() => setActiveTab(savedSedes.length === 0 ? TABS.SEDES : TABS.CELULARES)}
                >
                    Ir a {savedSedes.length === 0 ? "Sedes" : "Celulares"}
                </Button>
            }
        >
            Para crear un asesor necesitas {missingAsesorRequirements.join(" y ")} registrados.
        </Alert>
    );

    return (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", my: 3 }}>
            <Box px={3} pt={2}>
                <Typography variant="h5" fontWeight={600} color={PRIMARY_COLOR}>
                    Sedes, celulares y asesores
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Estos datos se guardan al instante; no necesitas usar "Guardar cambios".
                </Typography>
            </Box>
            <Tabs
                value={activeTab}
                onChange={(event, tab) => setActiveTab(tab)}
                variant="fullWidth"
                sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    mt: 1,
                    "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
                    "& .Mui-selected": { color: `${PRIMARY_COLOR} !important` },
                    "& .MuiTabs-indicator": { backgroundColor: PRIMARY_COLOR },
                }}
            >
                <Tab icon={<StorefrontIcon />} iconPosition="start" label={`Sedes (${sedes.length})`} />
                <Tab icon={<PhoneIphoneIcon />} iconPosition="start" label={`Celulares (${cellphones.length})`} />
                <Tab icon={<PersonIcon />} iconPosition="start" label={`Asesores (${asesores.length})`} />
            </Tabs>

            <Box p={3}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        {activeTab === TABS.SEDES && (
                            <RelationPanel
                                description="Puntos de venta del comercio."
                                addLabel="Agregar sede"
                                onAdd={() => openCreate(TABS.SEDES)}
                                items={sortedSedes}
                                renderItem={renderSede}
                                emptyMessage="Aún no hay sedes. Agrega la primera para poder asignar asesores."
                                viewAllLabel="Ver todas las sedes"
                                viewAllTitle="Sedes"
                                getSearchFields={getSedeSearchFields}
                                searchPlaceholder="Nombre, dirección, ciudad o celular"
                            />
                        )}
                        {activeTab === TABS.CELULARES && (
                            <RelationPanel
                                description="Números desde los que los asesores atienden a los clientes."
                                addLabel="Agregar celular"
                                onAdd={() => openCreate(TABS.CELULARES)}
                                items={sortedCellphones}
                                renderItem={renderCellphone}
                                emptyMessage="Aún no hay celulares. Agrega el primero para poder asignar asesores."
                                viewAllLabel="Ver todos los celulares"
                                viewAllTitle="Celulares"
                                getSearchFields={getCellphoneSearchFields}
                                searchPlaceholder="Número de celular"
                            />
                        )}
                        {activeTab === TABS.ASESORES && (
                            <RelationPanel
                                description="Personas que atienden en cada sede."
                                addLabel="Agregar asesor"
                                onAdd={() => openCreate(TABS.ASESORES)}
                                addDisabledReason={asesorDisabledReason}
                                notice={asesorRequirementsNotice}
                                items={sortedAsesores}
                                renderItem={renderAsesor}
                                emptyMessage="Aún no hay asesores registrados."
                                viewAllLabel="Ver todos los asesores"
                                viewAllTitle="Asesores"
                                getSearchFields={getAsesorSearchFields}
                                searchPlaceholder="Nombre, documento, email, sede o celular"
                            />
                        )}
                    </>
                )}
            </Box>

            {dialog?.type === TABS.SEDES && (
                <SedeFormDialog
                    open
                    sede={editingItem}
                    onClose={closeDialog}
                    onSubmit={editingItem ? handleUpdateSede : handleCreateSede}
                    cities={cities}
                    departaments={departaments}
                />
            )}
            {dialog?.type === TABS.CELULARES && (
                <CellphoneFormDialog
                    open
                    cellphone={editingItem}
                    onClose={closeDialog}
                    onSubmit={editingItem ? handleUpdateCellphone : handleCreateCellphone}
                    existingNumbers={cellphones
                        .filter((cellphone) => cellphone.uuid !== editingItem?.uuid)
                        .map((cellphone) => cellphone.number)}
                />
            )}
            {dialog?.type === TABS.ASESORES && (
                <AsesorFormDialog
                    open
                    asesor={editingItem}
                    onClose={closeDialog}
                    onSubmit={editingItem ? handleUpdateAsesor : handleCreateAsesor}
                    sedes={savedSedes}
                    cellphones={savedCellphones}
                />
            )}

            <Snackbar
                open={Boolean(successMessage)}
                autoHideDuration={SNACKBAR_DURATION_MS}
                onClose={() => setSuccessMessage("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" variant="filled" onClose={() => setSuccessMessage("")}>
                    {successMessage}
                </Alert>
            </Snackbar>
        </Paper>
    );
}
